import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Divider
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import StatusBadge from '../crm/StatusBadge';
import Sparkline from '../crm/Sparkline';

const ClinicDrawer = ({ open, onClose, clinic, org }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    if (clinic) {
      // Generate mock data for the clinic
      setOrders(generateMockOrders());
      setContacts(generateMockContacts());
      setNotes(generateMockNotes());
    }
  }, [clinic]);

  const generateMockOrders = () => {
    return Array.from({ length: 6 }, (_, i) => ({
      id: `order-${i + 1}`,
      date: new Date(Date.now() - i * 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      units: Math.floor(5 + Math.random() * 25),
      status: i === 0 ? 'Delivered' : 'Completed',
      total: Math.floor(500 + Math.random() * 2000)
    }));
  };

  const generateMockContacts = () => {
    const contacts = ['Dr. Sarah Johnson', 'Maria Rodriguez, RN', 'David Chen, Admin'];
    return contacts.map((name, i) => ({
      id: `contact-${i + 1}`,
      name,
      role: name.includes('Dr.') ? 'Physician' : name.includes('RN') ? 'Nurse' : 'Administrator',
      email: name.toLowerCase().replace(/[^a-z ]/g, '').replace(/ /g, '.') + '@clinic.com',
      phone: `(555) ${Math.floor(100 + Math.random() * 900)}-${Math.floor(1000 + Math.random() * 9000)}`
    }));
  };

  const generateMockNotes = () => {
    return [
      {
        id: 'note-1',
        date: '2024-01-15',
        author: 'Sarah Johnson',
        text: 'Discussed expansion of treatment rooms. Interested in additional equipment for Q2.'
      },
      {
        id: 'note-2', 
        date: '2024-01-10',
        author: 'Mike Chen',
        text: 'Quality review completed. All standards met. Next audit scheduled for March.'
      }
    ];
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      const note = {
        id: `note-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        author: 'Current User',
        text: newNote
      };
      setNotes([note, ...notes]);
      setNewNote('');
    }
  };

  const unitsData = [12, 15, 8, 22, 18, 25, 19, 14, 28, 22, 16, 20]; // Mock 12-month data

  if (!clinic) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 500 },
          bgcolor: 'background.default'
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pt: { xs: 8, md: 3 } }}>
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: org === 'bare' ? '#f0f4f0' : '#f0fdfa'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {clinic.name}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body1" sx={{ color: '#64748b', mb: 2 }}>
            {clinic.city}, {clinic.state}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <StatusBadge status={clinic.status} />
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {clinic.units30d} units (30d) • {clinic.units90d} units (90d)
            </Typography>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Summary" />
          <Tab label="Contacts" />
          <Tab label="Notes" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
          {activeTab === 0 && (
            <Box>
              {/* Units Trend */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Units Trend (12 months)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Sparkline 
                    data={unitsData} 
                    width={120} 
                    height={40}
                    color={org === 'bare' ? '#5c725c' : '#0d9488'}
                  />
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Avg: {Math.round(unitsData.reduce((a, b) => a + b) / unitsData.length)} units/month
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Recent Orders */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Recent Orders
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Units</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map(order => (
                      <TableRow key={order.id}>
                        <TableCell>{order.date}</TableCell>
                        <TableCell>{order.units}</TableCell>
                        <TableCell>${order.total.toLocaleString()}</TableCell>
                        <TableCell>
                          <StatusBadge status={order.status} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 3 }} />

              {/* Inventory Preferences */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Inventory Preferences
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                Preferred Products: {org === 'bare' ? 'Lipoderma Standard, Processing Supplies' : 'Medical Devices, Surgical Supplies'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                Order Frequency: Every 2 weeks
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Special Requirements: Quality certificates required, temperature-controlled storage
              </Typography>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Contacts ({contacts.length})
                </Typography>
                <Button variant="outlined" startIcon={<AddIcon />} size="small">
                  Add Contact
                </Button>
              </Box>
              
              {contacts.map(contact => (
                <Box 
                  key={contact.id} 
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 1 
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {contact.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                    {contact.role}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                    {contact.email} • {contact.phone}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Add Note
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this clinic..."
                  sx={{ mb: 2 }}
                />
                <Button 
                  variant="contained" 
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  sx={{ 
                    bgcolor: org === 'bare' ? '#5c725c' : '#0d9488',
                    '&:hover': {
                      bgcolor: org === 'bare' ? '#4a5d4a' : '#0b7a6e'
                    }
                  }}
                >
                  Add Note
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Recent Notes
              </Typography>
              
              {notes.map(note => (
                <Box 
                  key={note.id} 
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    bgcolor: '#f8fafc', 
                    borderRadius: 1,
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {note.author}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {note.date}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {note.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ClinicDrawer;
